<script setup lang="ts">
/**
 * Knowledge results: pages and documents together, facets over the whole
 * result set (type, lineage, tag, source, status), real pages (offset), the
 * query highlighted in snippets. Nothing found → close names ("did you mean"),
 * a resolved reference, "create this page" and "ask instead".
 */
import { ArrowRight, ChevronLeft, ChevronRight, FilePlus, Link2, SearchX, SlidersHorizontal, Sparkles, X } from "@lucide/vue";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useKnowledgeSearch, useResolveRef } from "@/api/knowledge";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import CreateEntityDialog from "@/components/knowledge/CreateEntityDialog.vue";
import EntityLink from "@/components/knowledge/EntityLink.vue";
import KnowledgeNav from "@/components/knowledge/KnowledgeNav.vue";
import KnowledgeSearchBox from "@/components/knowledge/KnowledgeSearchBox.vue";
import SearchFacets from "@/components/knowledge/SearchFacets.vue";
import SearchHitItem from "@/components/knowledge/SearchHitItem.vue";
import { Button } from "@/components/ui/button";
import { useRouteQuery, useRouteQueryList } from "@/composables/useRouteQuery";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import { formatNumber } from "@/lib/format";
import { DOCUMENT_INFO, entityPath, typeInfo } from "@/lib/knowledge";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Search knowledge", requires: { capability: "knowledge.read" } } });

const PAGE_SIZE = 20;
const MAX_OFFSET = 1000;

const router = useRouter();
const { can } = useSession();
const q = useRouteQuery("q");
const types = useRouteQueryList("types");
const lineages = useRouteQueryList("lineage");
const tags = useRouteQueryList("tags");
const sources = useRouteQueryList("sources");
const status = useRouteQueryList("status");
const pageParam = useRouteQuery("page", "1");
const page = computed(() => Math.max(1, Number.parseInt(pageParam.value, 10) || 1));

const draft = ref(q.value);
watch(q, (v) => (draft.value = v));

// A new query or filter starts at page 1 (one navigation, so history stays clean).
function setQuery(patch: Record<string, string | string[] | undefined>) {
  const query = { ...router.currentRoute.value.query };
  for (const [k, v] of Object.entries(patch)) {
    const value = Array.isArray(v) ? v.join(",") : v;
    if (value) query[k] = value;
    else delete query[k];
  }
  delete query.page;
  void router.replace({ query });
}
const listModel = (name: string, source: { value: string[] }) =>
  computed({ get: () => source.value, set: (v: string[]) => setQuery({ [name]: v }) });
const typesModel = listModel("types", types);
const lineagesModel = listModel("lineage", lineages);
const tagsModel = listModel("tags", tags);
const sourcesModel = listModel("sources", sources);
const statusModel = listModel("status", status);

const params = computed(() => ({
  q: q.value,
  types: types.value,
  lineage: lineages.value.join(","),
  tags: tags.value,
  sources: sources.value,
  status: status.value,
  limit: PAGE_SIZE,
  offset: Math.min((page.value - 1) * PAGE_SIZE, MAX_OFFSET),
  facets: true,
}));
const search = useKnowledgeSearch(params);
const data = computed(() => search.data.value);
const hasInput = computed(() => Boolean(q.value || types.value.length || tags.value.length || lineages.value.length || sources.value.length));
const terms = computed(() => q.value.split(/\s+/).filter(Boolean));
const total = computed(() => data.value?.total ?? 0);
const pages = computed(() => Math.max(1, Math.ceil(Math.min(total.value, MAX_OFFSET + PAGE_SIZE) / PAGE_SIZE)));
function goTo(p: number) {
  pageParam.value = p <= 1 ? "" : String(p);
  window.scrollTo({ top: 0, behavior: "smooth" });
}

// Nothing found: maybe the query is a reference (gvk:kg://…, github_repo:…, knowledge://…).
const looksLikeRef = computed(() => /[:/]/.test(q.value) && !/\s/.test(q.value.trim()));
const empty = computed(() => Boolean(data.value) && total.value === 0);
const resolved = useResolveRef(q, () => empty.value && looksLikeRef.value);

// Active filters as removable chips
const activeFilters = computed(() => {
  const sourceLabel = (id: string) => data.value?.facets?.sources.find((s) => s.value === id)?.label ?? "Source";
  return [
    ...types.value.map((v) => ({ key: `t:${v}`, label: v === "document" ? DOCUMENT_INFO.plural : typeInfo(v).plural || v, remove: () => setQuery({ types: types.value.filter((x) => x !== v) }) })),
    ...lineages.value.map((v) => ({ key: `l:${v}`, label: v, remove: () => setQuery({ lineage: lineages.value.filter((x) => x !== v) }) })),
    ...tags.value.map((v) => ({ key: `g:${v}`, label: `#${v}`, remove: () => setQuery({ tags: tags.value.filter((x) => x !== v) }) })),
    ...sources.value.map((v) => ({ key: `s:${v}`, label: sourceLabel(v), remove: () => setQuery({ sources: sources.value.filter((x) => x !== v) }) })),
    ...status.value.map((v) => ({ key: `st:${v}`, label: v, remove: () => setQuery({ status: status.value.filter((x) => x !== v) }) })),
  ];
});
const hasFilters = computed(() => activeFilters.value.length > 0);
function clearFilters() {
  setQuery({ types: undefined, lineage: undefined, tags: undefined, sources: undefined, status: undefined });
}

const filtersOpen = ref(false);
const createOpen = ref(false);
const createType = computed(() => (types.value.length === 1 && types.value[0] !== "document" ? types.value[0] : undefined));
</script>

<template>
  <div>
    <PageHeader :title="t.nav.search" :description="t.knowledge.searchIntro">
      <template #actions>
        <Button v-if="can('knowledge.write')" variant="outline" @click="createOpen = true"><FilePlus /> {{ t.knowledge.create }}</Button>
      </template>
    </PageHeader>
    <KnowledgeNav />

    <KnowledgeSearchBox v-model="draft" :navigate="false" :autofocus="!q" class="mb-6" @submit="(v) => setQuery({ q: v })" />

    <div class="grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)]">
      <!-- Facets: a sidebar on large screens, a toggle on small ones -->
      <aside class="min-w-0" :aria-label="t.knowledge.filters">
        <Button variant="outline" size="sm" class="mb-3 lg:hidden" :aria-expanded="filtersOpen" @click="filtersOpen = !filtersOpen">
          <SlidersHorizontal /> {{ t.knowledge.filters }}<span v-if="hasFilters" class="rounded-full bg-primary px-1.5 text-[11px] text-primary-foreground">{{ activeFilters.length }}</span>
        </Button>
        <div :class="cn('lg:sticky lg:top-4 lg:block', filtersOpen ? 'block rounded-lg border bg-card p-4 lg:border-0 lg:bg-transparent lg:p-0' : 'hidden')">
          <SearchFacets
            v-if="hasInput"
            v-model:types="typesModel"
            v-model:lineages="lineagesModel"
            v-model:tags="tagsModel"
            v-model:sources="sourcesModel"
            v-model:status="statusModel"
            :facets="data?.facets"
            :loading="search.isFetching.value"
          />
        </div>
      </aside>

      <section class="min-w-0" aria-label="Results">
        <div v-if="hasFilters" class="mb-3 flex flex-wrap items-center gap-1.5">
          <button
            v-for="f in activeFilters"
            :key="f.key"
            type="button"
            class="inline-flex h-7 items-center gap-1 rounded-full border bg-accent px-2.5 text-xs font-medium text-accent-foreground hover:border-primary/40"
            :aria-label="`Remove filter ${f.label}`"
            @click="f.remove()"
          >
            {{ f.label }} <X class="size-3" aria-hidden="true" />
          </button>
          <Button variant="ghost" size="sm" @click="clearFilters">{{ t.common.clear }}</Button>
        </div>

        <EmptyState v-if="!hasInput" :icon="SearchX" title="Search for anything" description="Type a name, a question or an error message — or pick a type on the overview.">
          <Button as-child variant="outline"><RouterLink to="/knowledge">{{ t.nav.overview }}</RouterLink></Button>
        </EmptyState>
        <ListSkeleton v-else-if="search.isPending.value" :rows="6" />
        <ErrorState v-else-if="search.error.value" :error="search.error.value" @retry="search.refetch()" />

        <div v-else-if="empty" class="flex flex-col gap-4">
          <RouterLink
            v-if="resolved.data.value"
            :to="entityPath(resolved.data.value.type, resolved.data.value.slug)"
            class="flex items-center gap-3 rounded-lg border border-info/30 bg-info-soft px-4 py-3 text-sm hover:border-info/60"
          >
            <Link2 class="size-4 text-info" aria-hidden="true" />
            <span class="flex-1">{{ t.knowledge.resolvedRef }} <strong>{{ resolved.data.value.title }}</strong> ({{ typeInfo(resolved.data.value.type).label }})</span>
            <ArrowRight class="size-4" aria-hidden="true" />
          </RouterLink>
          <EmptyState :icon="SearchX" :title="t.knowledge.searchEmpty" :description="t.knowledge.searchEmptyHint">
            <Button v-if="can('knowledge.write') && q" @click="createOpen = true"><FilePlus /> {{ t.knowledge.createPageFor(q) }}</Button>
            <Button v-if="q" as-child variant="outline"><RouterLink :to="{ path: '/ask', query: { q } }"><Sparkles /> {{ t.knowledge.askInstead }}</RouterLink></Button>
            <Button v-if="hasFilters" variant="ghost" @click="clearFilters">{{ t.common.clear }} filters</Button>
          </EmptyState>
          <div v-if="data?.didYouMean?.length" class="rounded-lg border bg-card px-4 py-3">
            <p class="mb-1.5 text-sm font-medium">{{ t.knowledge.didYouMean }}</p>
            <ul class="flex flex-col">
              <li v-for="d in data.didYouMean" :key="d.uri">
                <EntityLink :entity="d"><span class="text-xs text-muted-foreground">{{ typeInfo(d.type).label }}</span></EntityLink>
              </li>
            </ul>
          </div>
        </div>

        <template v-else-if="data">
          <p class="mb-3 text-sm text-muted-foreground" aria-live="polite">
            {{ t.knowledge.results(total, formatNumber(total)) }}
            <template v-if="pages > 1"> · page {{ page }} of {{ pages }}</template>
            <span v-if="search.isFetching.value" class="ml-1 opacity-60">· updating…</span>
            <span v-for="note in data.notes ?? []" :key="note" class="block text-xs">{{ note }}</span>
          </p>
          <ul :class="cn('flex flex-col gap-2 transition-opacity', search.isFetching.value && 'opacity-70')">
            <li v-for="hit in data.hits" :key="hit.uri"><SearchHitItem :hit="hit" :terms="terms" /></li>
          </ul>

          <nav v-if="pages > 1" aria-label="Pages" class="mt-5 flex items-center justify-between gap-2">
            <Button variant="outline" size="sm" :disabled="page <= 1 || search.isFetching.value" @click="goTo(page - 1)"><ChevronLeft /> Previous</Button>
            <span class="text-sm text-muted-foreground tabular-nums">{{ page }} / {{ pages }}</span>
            <Button variant="outline" size="sm" :disabled="page >= pages || search.isFetching.value" @click="goTo(page + 1)">Next <ChevronRight /></Button>
          </nav>

          <p v-if="q && can('knowledge.write') && page >= pages" class="mt-6 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            Not what you were looking for?
            <Button variant="link" size="sm" class="h-auto p-0" @click="createOpen = true">{{ t.knowledge.createPageFor(q) }}</Button>
          </p>
        </template>
      </section>
    </div>

    <CreateEntityDialog v-model:open="createOpen" :initial-name="q" :initial-type="createType" />
  </div>
</template>
